<template>
  <div class="service-content">
    <div class="service-details-container">
      <div class="service-details-container-header">
        {{ titleText }}
        <AuditInformation 
          v-if="!loading && service && service.id > 0 && service.createdByUser && service.createdDate && service.lastModifiedByUser && service.lastModifiedDate"
          :created-by-user="service.createdByUser"
          :created-date="service.createdDate"
          :last-modified-by-user="service.lastModifiedByUser"
          :last-modified-date="service.lastModifiedDate"
        />
      </div>
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-if="!loading && service"
        class="service-details-container-content"
      >
        <LockingButton
          class="save-button"
          :disabled="!isDirty"
          :locked="saving"
          :text="'Save'"
          @click="onSaveServiceClicked"
        />
        <TextInput
          v-model="service.name"
          :show-required="true"
          label="Name"
          class="input"
        />
        <CurrencyInput
          v-model="service.price"
          :show-required="true"
          label="Price"
          class="input"
        />
        <TextInput
          v-model="service.sku"
          :show-required="true"
          label="Sku"
          class="input"
        />
        <Checkbox
          v-model="service.allowDiscounts"
          label="Allow Discounts?"
          class="input"
        />
        <Dropdown
          v-if="!service || service.id === 0"
          v-model="service.buildStepId"
          label="Build Step"
          :show-required="true"
          :options="buildStepDropdownOptions"
          class="input"
        />
        <TextInput
          v-else
          v-model="buildStepText"
          :disabled="true"
          label="Build Step"
        />
        <TextArea
          v-model="service.description"
          label="Description"
          class="input"
        />
      </div>
    </div>
    <div class="fields-container">
      <div class="fields-container-header">
        <div class="fields-container-header-text">
          Fields
        </div>
        <a
          v-show="!loading && service && service.id > 0"
          class="add-field"
          @click="onAddFieldClicked"
        >
          Create
        </a>
      </div>
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-if="!loading"
        class="overflow-wrapper"
      >
        <table>
          <thead>
            <tr>
              <th class="no-sort">
                <div>
                  Name
                </div>
              </th>
              <th class="no-sort">
                <div>
                  Type
                </div>
              </th>
              <th class="no-sort" />
            </tr>
          </thead>
          <tbody v-if="service && service.serviceFields.length > 0">
            <tr
              v-for="serviceField in service.serviceFields"
              :key="serviceField.id"
              class="clickable"
              @click="onTableRowClicked(serviceField)"
            >
              <td>{{ serviceField.name }}</td>
              <td>{{ typeToDisplayValue(serviceField.type) }}</td>
              <td class="delete">
                <TrashCanSvg @click.stop="onDeleteClicked(serviceField.id)" />
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td
                v-if="service && service.id > 0"
                :colspan="3"
                class="message"
              >
                There are no fields on the service. Please click 'Create' above to add a field.
              </td>
              <td
                v-else
                :colspan="3"
                class="message"
              >
                Fields can be added to the service once it is initally created.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <transition name="slide-up">
      <ServiceFieldModal
        v-if="showServiceFieldModal && serviceFieldToEdit"
        :service-field="serviceFieldToEdit"
        :service-id="id"
        @service-field-changed="getService"
        @close="showServiceFieldModal = false"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import Checkbox from '@/components/shared/Checkbox.vue'
import CurrencyInput from '@/components/shared/CurrencyInput.vue'
import Dropdown from '@/components/shared/Dropdown.vue'
import TextArea from '@/components/shared/TextArea.vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { ServiceService } from '@/services/service.service'
import { ServiceDetails } from '@/@types/responses/service/ServiceDetails'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import { BuildStepService } from '@/services/buildStep.service'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import { ServiceFieldService } from '@/services/serviceField.service'
import { ServiceField } from '@/@types/responses/service/ServiceField'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import ServiceFieldModal from '@/components/modals/ServiceFieldModal.vue'
import { CreateServiceMessage } from '@/@types/messages/CreateServiceMessage'
import { UpdateServiceMessage } from '@/@types/messages/UpdateServiceMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'Service',
  components: {
    AuditInformation,
    Checkbox,
    CurrencyInput,
    Dropdown,
    LoadingPulse,
    LockingButton,
    ServiceFieldModal,
    TextArea,
    TextInput,
    TrashCanSvg
  },
  beforeRouteLeave() {
    if (this.showServiceFieldModal) {
      this.showServiceFieldModal = false
      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const buildStepService = inject(PROVIDER_CONSTANTS.buildStepService) as BuildStepService
    const serviceFieldService = inject(PROVIDER_CONSTANTS.serviceFieldService) as ServiceFieldService
    const serviceService = inject(PROVIDER_CONSTANTS.serviceService) as ServiceService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      buildStepService,
      serviceFieldService,
      serviceService,
      toast
    }
  },
  data() {
    let service: ServiceDetails|undefined
    let serviceFieldToEdit: ServiceField|undefined
    let unchangedService: ServiceDetails|undefined

    return {
      loading: true,
      buildStepDropdownOptions: [] as DropdownOptionResponse<number>[],
      service,
      saving: false,
      serviceFieldToEdit,
      showServiceFieldModal: false,
      unchangedService,
    }
  },
  computed: {
    buildStepText(): string {
      if (!this.service) return ''

      const index = this.buildStepDropdownOptions.findIndex(t => t.id === this.service?.buildStepId)
      if (index === -1) return ''

      return this.buildStepDropdownOptions[index].name
    },
    isDirty(): boolean {
      const a = this.service
      const b = this.unchangedService

      return (a?.name !== b?.name ||
             a?.description !== b?.description ||
             a?.price !== b?.price ||
             a?.sku !== b?.sku ||
             a?.buildStepId !== b?.buildStepId ||
             a?.allowDiscounts !== b?.allowDiscounts) && (a?.buildStepId !== undefined && a?.name.trim() !== '' && a?.sku.trim() !== '' && a?.price >= 0)
    },
    titleText(): string {
      return !this.service || this.service.id === 0 ? 'Create Service' : 'Edit Service'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    this.getBuildSteps().then(() => {
      if (this.id > 0) {
        this.getService()
      } else {
        this.service = new ServiceDetails(0)
        this.unchangedService = new ServiceDetails(0)
        this.loading = false
      }
    })
  },
  methods: {
    getBuildSteps(): Promise<void> {
      return this.buildStepService.getBuildStepDropdownOptions().then(dropdownOptionsResponse => {
        this.buildStepDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build steps. Try reloading the page.')
        }
      })
    },
    getService(): Promise<void> {
      return this.serviceService.getService(this.service ? this.service.id : this.id).then(service => {
        this.service = service
        this.unchangedService = JSON.parse(JSON.stringify(service))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the service. Try reloading the page.')
        }
      })
    },
    onAddFieldClicked(): void {
      this.serviceFieldToEdit = new ServiceField(0)
      this.showServiceFieldModal = true
    },
    onDeleteClicked(id: number): void {
      this.serviceFieldService.deleteServiceField(id).then(() => {
        this.getService().then(() => {
          this.toast.success('Field deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the field. Please try again.')
        }
      })
    },
    onSaveServiceClicked(): void {
      if (!this.service || !this.service.buildStepId) return

      this.saving = true

      if (this.service.id === 0) {
        const createServiceMessage = new CreateServiceMessage(this.service.allowDiscounts, this.service.buildStepId, this.service.description, this.service.name, this.service.price, this.service.sku)
        this.serviceService.createService(createServiceMessage).then(objectCreatedResponse => {
          if (!this.service) return

          this.service.id = objectCreatedResponse.id
          this.getService().then(() => {
            this.$router.replace(`/service/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The service was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the service. Please try again.')
          }
        })
      } else {
        const updateServiceMessage = new UpdateServiceMessage(this.service.allowDiscounts, this.service.description, this.service.name, this.service.price, this.service.sku)
        this.serviceService.updateService(this.id, updateServiceMessage).then(() => {
          this.toast.success('The service was updated successfully.')
          this.$router.push('/services')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the service. Please try again.')
          }
        })
      }
    },
    onTableRowClicked(serviceField: ServiceField): void {
      this.serviceFieldToEdit = serviceField
      this.showServiceFieldModal = true
    },
    typeToDisplayValue(type: number): string {
      switch(type) {
        case 1: return 'Cartridge'
        case 2: return 'Selection'
        default: return ''
      }
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.service)
        document.title = `${this.service.name} - Short Action Customs, LLC`
    }
  }
})
</script>

<style scoped lang="scss">
.service-content {
  display: flex;
  flex-direction: column;
  padding: 15px;

  @include desktop {
    flex-direction: row;
  }

  > .service-details-container {
    display: flex;
    flex-direction: column;
    margin-bottom: 15px;
    position: relative;

    @include desktop {
      flex: 1 1 35%;
      height: max-content;
      padding: 15px;
      background-color: $Color-fill-gray;
      border-radius: 2px;
      @include element-shadow;
    }

    > .service-details-container-header {
      line-height: 37px;
      font-size: 24px;
      font-weight: bold;
      padding-bottom: 10px;
      border-bottom: 1px solid $Color-border-gray;
    }

    > .loading-spinner {
      margin: auto;
      padding: 20px;
    }

    > .service-details-container-content {
      padding-top: 15px;

      > .save-button {
        position: absolute;
        top: 0;
        right: 0;

        @include desktop {
          top: 15px;
          right: 15px;
        }
      }

      > .input {
        margin-bottom: 15px;
      }
    }
  }

  > .fields-container {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding-bottom: 15px;

    @include desktop {
      flex: 1 1 65%;
      width: 65%;
      padding-left: 20px;
    }

    > .fields-container-header {
      display: flex;
      margin-bottom: 15px;

      > .fields-container-header-text {
        margin: 0 auto 0 0;
        font-size: 24px;
        font-weight: bold;
      }

      > .add-field {
        @include flat-button;
        margin: auto 0;
      }
    }

    > .loading-spinner {
      margin: auto;
    }

    > .overflow-wrapper {
      overflow-y: auto;
      @include sac-table;
    }
  }

  > .slide-up {
    @include modal-animation;
  }
}
</style>
